import type { FetchError } from '@execonline-inc/fetch.private';
import type { UseFetch } from '@execonline-inc/react-hooks.private';
import { always } from '@kofno/piper';
import { autorun, makeAutoObservable, observable, runInAction } from 'mobx';
import { Task } from 'taskarian';
import { currentUserStore } from '../CurrentUserStore';
import { findLinkByT } from '../Fetch';
import { declareEndpoint, MissingLinkError } from '../Links';
import * as Resourceable from '../Resourceable';
import ResourceableInit from '../Resourceable/Init';
import { createDisposableBehavior } from './DisposableBehavior';

type CollectionError = FetchError | MissingLinkError;
type CollectionResponse = Resourceable.ParsedResponse<'/all_experiences', 'get'>;
type State = UseFetch.State<CollectionError, CollectionResponse>;

function createFetchTask() {
  const findLinkAndDeclare = findLinkByT(
    {
      rel: 'all-experiences',
      method: 'get',
    },
    currentUserStore.links,
  ).map(declareEndpoint('/all_experiences'));

  return Task.succeed<CollectionError, {}>({})
    .andThen(always(findLinkAndDeclare))
    .andThen(ResourceableInit.fetch);
}

class LearningCollectionsStore {
  @observable state: State = { kind: 'loading' } as const;
  private collectionsBehavior = createDisposableBehavior();
  private lastFetchTime: number | null = null;
  private readonly staleTime = 3600000; // 1 hour in milliseconds

  constructor() {
    makeAutoObservable(this);
  }

  private isStale(): boolean {
    if (!this.lastFetchTime) return true;
    return Date.now() - this.lastFetchTime > this.staleTime;
  }

  private fetchFreshData(): void {
    createFetchTask().fork(this.setErrorState, (data) => {
      this.setReadyState(data);
      runInAction(() => {
        this.lastFetchTime = Date.now();
      });
    });
  }

  initialize(): void {
    this.collectionsBehavior.initialize(() =>
      autorun(() => {
        if (currentUserStore.currentUserState.kind === 'identified') {
          if (this.isStale()) {
            this.fetchFreshData();
          }
        }
      }),
    );
  }

  dispose(): void {
    this.collectionsBehavior.dispose();
  }

  private setErrorState = (error: CollectionError) => {
    this.state = { kind: 'errored', error };
  };

  private setReadyState = (data: CollectionResponse) => {
    this.state = { kind: 'ready', data };
  };
}

const learningCollectionsStore = new LearningCollectionsStore();
learningCollectionsStore.initialize();

export { learningCollectionsStore };
